/**
 * VIEWS & MOTION — the feedback layer every screen shares.
 *
 *   showToast        a stacked, self-dismissing notice with an icon slot, a
 *                    sound and a live-region announcement.
 *   createSkeleton   shimmering placeholder rows/cards while data loads.
 *   createEmptyState an illustration slot, a title, a line and an optional
 *                    action — never a bare "nothing here".
 *   attachTooltip    a hover/focus/long-press tip, keyboard dismissible.
 *   countUp          a number that rolls to its new value (currencies).
 *   staggerIn        children arrive one after another, capped.
 *   attachTabIndicator  a gold underline that SLIDES between tabs, plus arrow
 *                    key / Home / End roving focus on a role=tablist.
 *
 * Every motion here respects prefers-reduced-motion (through `motionReduced`,
 * the one place the question is asked) by landing the end state at once.
 * Only transform/opacity/scale/translate move. CSS lives in FEEDBACK_CSS,
 * injected once per document; no backticks inside it (template literal).
 */
import { playUiSound } from '../audio/ui-sounds';
import { el } from '../layout/dom';

import { createSlotIcon, isIconSlot, type IconSlot } from './icon-registry';
import { enterRow, motionReduced, runMotion, type MotionOptions } from './motion';
import { attachPress } from './press';

/* ══════════════════════════════════════════════════════════════ styles ══ */

export const FEEDBACK_MARK = 'ui-feedback-css';

export const FEEDBACK_CSS = `
/* Toasts hang UNDER the game header (--rga-header-h, measured by the meta
   shell; 0 elsewhere) and stack downward with a gap, so a toast never covers
   the currency bar or another toast. On a desktop they sit at the header's
   right end, over the content column, not over the brand. */
.rga-toasts{position:fixed;z-index:80;left:50%;translate:-50% 0;
  top:calc(max(.75rem,env(safe-area-inset-top)) + var(--rga-header-h,0px));display:flex;flex-direction:column;gap:.5rem;
  width:min(26rem,calc(100vw - 1.5rem));pointer-events:none}
@media (min-width:48rem){.rga-toasts{left:auto;right:1rem;translate:none}}
.rga-toast{pointer-events:auto;display:flex;align-items:center;gap:.65rem;padding:.6rem .75rem .6rem .6rem;
  border-radius:var(--rga-radius-panel,14px);border:var(--rga-rim,2px) solid var(--rga-toast-rim,var(--color-border));
  background:var(--rga-face,none),var(--color-surface);color:var(--color-text);
  box-shadow:var(--rga-lift-1),var(--shadow-lift,0 10px 24px rgba(0,0,0,.45)),var(--rga-sheen);
  animation:rga-toast-in 260ms cubic-bezier(.34,1.56,.64,1)}
.rga-toast[data-tone=success]{--rga-toast-rim:var(--color-success)}
.rga-toast[data-tone=error]{--rga-toast-rim:var(--color-error)}
.rga-toast[data-tone=reward]{--rga-toast-rim:var(--color-gold)}
.rga-toast__body{flex:1 1 auto;min-width:0;font-size:.9rem;line-height:1.3}
.rga-toast__title{display:block;font-family:var(--font-heading,inherit);font-size:.95rem}
.rga-toast__close{flex:0 0 auto;width:2.75rem;height:2.75rem;margin:-.4rem -.4rem -.4rem 0;border:0;
  background:transparent;border-radius:10px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center}
.rga-toast.is-leaving{animation:rga-toast-out 180ms cubic-bezier(.4,0,1,1) forwards}
@keyframes rga-toast-in{0%{opacity:0;translate:0 -14px;scale:.94}100%{opacity:1;translate:0 0;scale:1}}
@keyframes rga-toast-out{to{opacity:0;translate:0 -10px;scale:.96}}

.rga-skel{display:block;border-radius:10px;
  background:linear-gradient(90deg,var(--color-surface) 0%,color-mix(in srgb,var(--color-surface) 80%,#fff) 45%,var(--color-surface) 90%);
  background-size:220% 100%;animation:rga-skel-shine 1.3s ease-in-out infinite}
.rga-skel--line{height:.85rem}
.rga-skel--title{height:1.3rem;width:55%}
.rga-skel--card{height:5.5rem;border-radius:14px}
.rga-skel--circle{width:2.75rem;height:2.75rem;border-radius:999px}
@keyframes rga-skel-shine{0%{background-position:120% 0}100%{background-position:-120% 0}}

.rga-empty{display:flex;flex-direction:column;align-items:center;gap:.5rem;text-align:center;
  padding:1.75rem 1rem;border-radius:16px;border:2px dashed color-mix(in srgb,var(--color-border) 60%,transparent)}
.rga-empty__art{width:7.5rem;height:7.5rem;display:flex;align-items:center;justify-content:center;
  filter:drop-shadow(0 4px 0 rgba(0,0,0,.45))}
.rga-empty__art img{width:100%;height:100%;object-fit:contain}
.rga-empty__title{font-family:var(--font-heading,inherit);font-size:1.1rem;color:var(--color-text)}
.rga-empty__body{font-size:.88rem;color:var(--color-text-dim);max-width:22rem}

.rga-tip{position:fixed;z-index:85;max-width:min(16rem,80vw);padding:.4rem .6rem;border-radius:10px;
  background:var(--rga-face,none),var(--color-surface-2);color:var(--color-text);font-size:.8rem;line-height:1.3;
  border:2px solid var(--color-border);box-shadow:var(--rga-lift-1),var(--rga-sheen);pointer-events:none;animation:rga-tip-in 140ms ease-out}
@keyframes rga-tip-in{0%{opacity:0;translate:0 4px}100%{opacity:1;translate:0 0}}

.rga-tabind-host{position:relative}
.rga-tabind{position:absolute;bottom:-2px;left:0;height:3px;width:var(--rga-tabind-w,0px);border-radius:3px;
  translate:var(--rga-tabind-x,0px) 0;background:var(--color-gold);
  box-shadow:0 0 10px color-mix(in srgb,var(--color-gold) 55%,transparent);pointer-events:none;
  transition:translate 260ms cubic-bezier(.34,1.3,.64,1),width 260ms cubic-bezier(.34,1.3,.64,1)}

@media (prefers-reduced-motion:reduce){
  .rga-toast,.rga-toast.is-leaving,.rga-tip{animation:none}
  .rga-skel{animation:none}
  .rga-tabind{transition:none}
}
`;

export function ensureFeedbackStyles(doc: Document = document): void {
  if (doc.querySelector(`style[data-rga="${FEEDBACK_MARK}"]`) !== null) return;
  const style = doc.createElement('style');
  style.setAttribute('data-rga', FEEDBACK_MARK);
  style.textContent = FEEDBACK_CSS;
  (doc.head ?? doc.documentElement).append(style);
}

/* ═══════════════════════════════════════════════════════════════ toasts ══ */

export type ToastTone = 'info' | 'success' | 'error' | 'reward';

export interface ToastOptions {
  readonly message: string;
  readonly title?: string;
  readonly tone?: ToastTone;
  /** Overrides the tone's icon. */
  readonly icon?: IconSlot;
  /** ms before it leaves on its own. 0 = stays until closed. Default 3200. */
  readonly durationMs?: number;
  /** Label for the close control (translated by the caller). */
  readonly closeLabel?: string;
  /** Play the tone's sound. Default true. */
  readonly sound?: boolean;
  /**
   * The screen already announces this through its own live region: hide the
   * toast from assistive technology so it is not read twice.
   */
  readonly visualOnly?: boolean;
  readonly doc?: Document;
}

export interface Toast {
  readonly root: HTMLElement;
  close(): void;
}

const TONE_ICON: Readonly<Record<ToastTone, IconSlot>> = Object.freeze({
  info: 'status-info',
  success: 'status-success',
  error: 'status-error',
  reward: 'action-claim',
});

const TONE_SOUND = Object.freeze({
  info: 'notify',
  success: 'confirm',
  error: 'error',
  reward: 'reward',
} as const);

/** Most toasts on screen at once; the oldest leaves when a new one arrives. */
export const TOAST_MAX = 3;
export const TOAST_DEFAULT_MS = 3200;

function toastHost(doc: Document): HTMLElement {
  let host = doc.querySelector<HTMLElement>('.rga-toasts');
  if (host === null) {
    host = el('div', {
      class: 'rga-toasts',
      'data-rga': 'toasts',
      // Polite: a toast never interrupts what a screen reader is saying.
      role: 'status',
      'aria-live': 'polite',
    });
    doc.body.append(host);
  }
  return host;
}

export function showToast(options: ToastOptions): Toast {
  const doc = options.doc ?? document;
  ensureFeedbackStyles(doc);
  const tone = options.tone ?? 'info';
  const host = toastHost(doc);

  const close = el('button', {
    type: 'button',
    class: 'rga-toast__close',
    'aria-label': options.closeLabel ?? 'Close',
    'data-sfx': 'none',
  }, [createSlotIcon(doc, 'header-close', { size: 18 })]);

  const body = el('div', { class: 'rga-toast__body' }, [
    options.title === undefined ? null : el('strong', { class: 'rga-toast__title', text: options.title }),
    el('span', { text: options.message }),
  ]);
  const root = el('div', { class: 'rga-toast', 'data-tone': tone, 'data-rga': 'toast' }, [
    createSlotIcon(doc, options.icon ?? TONE_ICON[tone], { size: 28 }),
    body,
    close,
  ]);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  const unpress = attachPress(close, { onPress: () => dismiss() });

  function dismiss(): void {
    if (closed) return;
    closed = true;
    if (timer !== null) clearTimeout(timer);
    unpress();
    if (motionReduced()) {
      root.remove();
      return;
    }
    root.classList.add('is-leaving');
    setTimeout(() => root.remove(), 200);
  }

  if (options.visualOnly === true) root.setAttribute('aria-hidden', 'true');
  host.append(root);
  while (host.children.length > TOAST_MAX) host.firstElementChild?.remove();
  if (options.sound !== false) playUiSound(TONE_SOUND[tone]);
  const ms = options.durationMs ?? TOAST_DEFAULT_MS;
  if (ms > 0) timer = setTimeout(dismiss, ms);
  return { root, close: dismiss };
}

/* ════════════════════════════════════════════════════════════ skeletons ══ */

export type SkeletonShape = 'line' | 'title' | 'card' | 'circle';

/**
 * A loading placeholder. `aria-hidden` — the caller's container carries
 * `aria-busy` and a sr-only "Loading" line; the shimmer is for eyes.
 */
export function createSkeleton(shapes: readonly SkeletonShape[] = ['title', 'line', 'line', 'card']): HTMLElement {
  ensureFeedbackStyles();
  return el(
    'div',
    { class: 'flex flex-col gap-2', 'aria-hidden': 'true', 'data-rga': 'skeleton' },
    shapes.map((shape, i) =>
      el('span', {
        class: `rga-skel rga-skel--${shape}`,
        style: shape === 'line' ? `width:${[92, 78, 64, 86][i % 4]}%` : undefined,
      }),
    ),
  );
}

/* ═════════════════════════════════════════════════════════ empty states ══ */

/** Where an empty-state illustration lands when generated. */
export const ILLUSTRATION_BASE = '/ui/illustrations/';

export interface EmptyStateOptions {
  /** Illustration id; looked for at /ui/illustrations/<id>.png. */
  readonly illustration: string;
  /** Icon slot shown big while the illustration does not exist yet. */
  readonly fallbackIcon: IconSlot;
  readonly title: string;
  readonly body?: string;
  readonly action?: HTMLElement;
  readonly testId?: string;
}

export function createEmptyState(options: EmptyStateOptions): HTMLElement {
  ensureFeedbackStyles();
  const art = el('div', { class: 'rga-empty__art', 'aria-hidden': 'true', 'data-illustration': options.illustration });
  const img = el('img', { src: `${ILLUSTRATION_BASE}${options.illustration}.png`, alt: '', decoding: 'async' });
  img.addEventListener('error', () => {
    art.replaceChildren(
      isIconSlot(options.fallbackIcon)
        ? createSlotIcon(document, options.fallbackIcon, { size: 96 })
        : el('span'),
    );
  });
  art.append(img);
  return el('div', { class: 'rga-empty', 'data-rga': options.testId ?? 'empty-state' }, [
    art,
    el('p', { class: 'rga-empty__title', text: options.title }),
    options.body === undefined ? null : el('p', { class: 'rga-empty__body', text: options.body }),
    options.action ?? null,
  ]);
}

/* ═════════════════════════════════════════════════════════════ tooltips ══ */

export interface TooltipOptions {
  /** Long-press time on touch before the tip shows. */
  readonly holdMs?: number;
}

/**
 * A short tip on hover (mouse), focus (keyboard) or long-press (touch).
 * The text also becomes the target's `aria-describedby`, so it is never a
 * sighted-only hint. Escape closes it. Returns a detach.
 */
let tipSeq = 0;

export function attachTooltip(target: HTMLElement, text: string, options: TooltipOptions = {}): () => void {
  ensureFeedbackStyles(target.ownerDocument);
  const doc = target.ownerDocument;
  const id = `rga-tip-${++tipSeq}`;
  const desc = el('span', { id, class: 'sr-only', text });
  target.append(desc);
  const prior = target.getAttribute('aria-describedby');
  target.setAttribute('aria-describedby', prior === null ? id : `${prior} ${id}`);

  let tip: HTMLElement | null = null;
  let hold: ReturnType<typeof setTimeout> | null = null;

  const show = (): void => {
    if (tip !== null) return;
    tip = el('div', { class: 'rga-tip', role: 'presentation', 'aria-hidden': 'true', text });
    doc.body.append(tip);
    const r = target.getBoundingClientRect();
    const w = tip.offsetWidth;
    const left = Math.max(8, Math.min(r.left + r.width / 2 - w / 2, (doc.defaultView?.innerWidth ?? 800) - w - 8));
    const above = r.top - tip.offsetHeight - 8;
    tip.style.left = `${left}px`;
    tip.style.top = `${above > 8 ? above : r.bottom + 8}px`;
  };
  const hide = (): void => {
    if (hold !== null) clearTimeout(hold);
    hold = null;
    tip?.remove();
    tip = null;
  };
  const onEnter = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') show();
  };
  const onDown = (e: PointerEvent): void => {
    if (e.pointerType !== 'mouse') hold = setTimeout(show, options.holdMs ?? 450);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') hide();
  };
  target.addEventListener('pointerenter', onEnter);
  target.addEventListener('pointerleave', hide);
  target.addEventListener('pointerdown', onDown);
  target.addEventListener('pointerup', hide);
  target.addEventListener('pointercancel', hide);
  target.addEventListener('focus', show);
  target.addEventListener('blur', hide);
  target.addEventListener('keydown', onKey);
  return () => {
    hide();
    desc.remove();
    if (prior === null) target.removeAttribute('aria-describedby');
    else target.setAttribute('aria-describedby', prior);
    target.removeEventListener('pointerenter', onEnter);
    target.removeEventListener('pointerleave', hide);
    target.removeEventListener('pointerdown', onDown);
    target.removeEventListener('pointerup', hide);
    target.removeEventListener('pointercancel', hide);
    target.removeEventListener('focus', show);
    target.removeEventListener('blur', hide);
    target.removeEventListener('keydown', onKey);
  };
}

/* ═════════════════════════════════════════════════════════════ count-up ══ */

export interface CountUpOptions extends MotionOptions {
  readonly durationMs?: number;
  /** Formats each frame's value. Defaults to toLocaleString. */
  readonly format?: (n: number) => string;
  /** rAF seam for tests. */
  readonly frame?: (cb: (t: number) => void) => void;
  readonly now?: () => number;
}

/**
 * Roll `node`'s text from `from` to `to`. Integers only (currencies), eased
 * out so the last digits settle visibly. Reduced motion, a zero delta or no
 * rAF lands the final value at once. Returns a cancel that lands it too.
 */
export function countUp(node: HTMLElement, from: number, to: number, options: CountUpOptions = {}): () => void {
  const format = options.format ?? ((n: number) => n.toLocaleString());
  const land = (): void => {
    node.textContent = format(to);
  };
  const frame =
    options.frame ??
    (typeof globalThis.requestAnimationFrame === 'function'
      ? (cb: (t: number) => void) => void globalThis.requestAnimationFrame(cb)
      : null);
  if (from === to || frame === null || motionReduced(options)) {
    land();
    options.onDone?.();
    return () => undefined;
  }
  const now = options.now ?? (() => performance.now());
  const duration = options.durationMs ?? Math.min(900, 300 + Math.abs(to - from) * 2);
  const start = now();
  let cancelled = false;
  node.setAttribute('data-counting', 'true');
  const step = (): void => {
    if (cancelled) return;
    const p = Math.min(1, (now() - start) / duration);
    const eased = 1 - (1 - p) ** 3;
    node.textContent = format(Math.round(from + (to - from) * eased));
    if (p < 1) frame(step);
    else {
      node.removeAttribute('data-counting');
      options.onDone?.();
    }
  };
  frame(step);
  return () => {
    cancelled = true;
    node.removeAttribute('data-counting');
    land();
  };
}

/* ══════════════════════════════════════════════════════════════ stagger ══ */

/**
 * Stagger a container's matching children in (enterRow's 22ms step, capped
 * at 240ms total so a long list still arrives fast). Children past `limit`
 * appear with the last one. Returns how many were animated.
 */
export function staggerIn(container: HTMLElement, selector = ':scope > *', limit = 14, options: MotionOptions = {}): number {
  if (motionReduced(options)) return 0;
  const nodes = [...container.querySelectorAll<HTMLElement>(selector)].slice(0, limit);
  nodes.forEach((node, i) =>
    enterRow(node, i, {
      ...options,
      // Strip what the last keyframe wrote inline: a leftover transform makes
      // the node a containing block for any fixed overlay inside it (the
      // trap enterScreen documents), and a leftover opacity beats a screen's
      // own disabled/dim styling.
      onDone: () => {
        node.style.removeProperty('transform');
        node.style.removeProperty('opacity');
        options.onDone?.();
      },
    }),
  );
  return nodes.length;
}

/** A one-shot "pop" for something that just changed value (a badge, a chip). */
export function pop(node: HTMLElement, options: MotionOptions = {}): void {
  runMotion(
    node,
    [{ scale: '1' }, { scale: '1.16', offset: 0.4 }, { scale: '1' }] as unknown as Keyframe[],
    { duration: 280, easing: 'cubic-bezier(.34,1.56,.64,1)' },
    options,
  );
}

/* ═══════════════════════════════════════════════════════ tab indicator ══ */

/**
 * The sliding underline and the keyboard model for a `role=tablist`.
 *
 * The indicator is ONE element that moves (translate + width) to whichever
 * tab is `aria-selected=true`, so switching tabs reads as the underline
 * travelling, not as two underlines blinking. Arrow keys move between tabs
 * (Home/End jump), activating each with a click so the screen's own handler
 * runs — the WAI-ARIA tabs pattern with automatic activation.
 *
 * Call `sync()` after the screen changes the selection without a rebuild.
 */
const lastTabPos = new Map<string, { x: number; w: number }>();

export function attachTabIndicator(tablist: HTMLElement, key?: string): { sync(): void; destroy(): void } {
  ensureFeedbackStyles(tablist.ownerDocument);
  tablist.classList.add('rga-tabind-host');
  const bar = el('span', { class: 'rga-tabind', 'aria-hidden': 'true', 'data-rga': 'tab-indicator' });
  tablist.append(bar);
  const tabs = (): HTMLElement[] => [...tablist.querySelectorAll<HTMLElement>('[role="tab"]')];

  const sync = (): void => {
    const sel = tabs().find((t) => t.getAttribute('aria-selected') === 'true');
    if (sel === undefined) {
      bar.style.opacity = '0';
      return;
    }
    bar.style.opacity = '1';
    const x = sel.offsetLeft;
    const w = sel.offsetWidth;
    bar.style.setProperty('--rga-tabind-x', `${x}px`);
    bar.style.setProperty('--rga-tabind-w', `${w}px`);
    if (key !== undefined && w > 0) lastTabPos.set(key, { x, w });
  };

  /*
   * A screen that REBUILDS its tab strip on every switch (most of them here)
   * would only ever show the bar appearing in its new place. With a key, the
   * bar starts where the previous strip left it and travels on the next
   * frame, so the slide survives the rebuild.
   */
  const from = key === undefined ? undefined : lastTabPos.get(key);
  if (from !== undefined && typeof globalThis.requestAnimationFrame === 'function') {
    bar.style.transition = 'none';
    bar.style.setProperty('--rga-tabind-x', `${from.x}px`);
    bar.style.setProperty('--rga-tabind-w', `${from.w}px`);
    globalThis.requestAnimationFrame(() => {
      bar.style.removeProperty('transition');
      sync();
    });
  }

  const onKey = (e: KeyboardEvent): void => {
    const list = tabs();
    const i = list.indexOf(e.target as HTMLElement);
    if (i < 0) return;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % list.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + list.length) % list.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = list.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const target = list[next] as HTMLElement;
    target.focus();
    target.click();
  };
  tablist.addEventListener('keydown', onKey);
  const ro =
    typeof ResizeObserver === 'function' ? new ResizeObserver(() => sync()) : null;
  ro?.observe(tablist);
  if (from === undefined) sync();
  return {
    sync,
    destroy() {
      tablist.removeEventListener('keydown', onKey);
      ro?.disconnect();
      bar.remove();
    },
  };
}
